import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Heart } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  INTERACTIVE_STATES,
  SIZE_SCALE,
  RADIUS_SCALE,
  COLOR_SCALE,
} from '@/components/design-system/tokens';
import { useUserProfile, queryKeys } from '@/hooks/queries';
import { useTranslation } from '@/hooks/useTranslation';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface FavoriteButtonProps {
  entityType: string;
  entityId: string | number;
  size?: 'small' | 'default' | 'large';
  className?: string;
  variant?: 'dark' | 'circle' | 'light';
}

// Size configurations
const getSizeConfig = (size: string) => {
  switch (size) {
    case 'small':
      return {
        button: `${SIZE_SCALE.w9} ${SIZE_SCALE['9']}`,
        icon: `${SIZE_SCALE.w4} ${SIZE_SCALE['4']}`,
      };
    case 'default':
    case 'large':
    default:
      return {
        button: `${SIZE_SCALE.w12} ${SIZE_SCALE['12']}`,
        icon: `${SIZE_SCALE.w6} ${SIZE_SCALE['6']}`,
      };
  }
};

// Helper function to get accessibility labels
const getAccessibilityLabels = (
  isFavorited: boolean,
  t: (page: string, context: string, fallback?: string) => string
) => {
  const label = isFavorited
    ? String(t('favoritebutton', 'remove_from_favorites', 'Remove from favorites'))
    : String(t('favoritebutton', 'add_to_favorites', 'Add to favorites'));
  return { label, title: label };
};

export default function FavoriteButton({
  entityType,
  entityId,
  size = 'default',
  className = '',
  variant = 'light',
}: FavoriteButtonProps) {
  const { t } = useTranslation() as {
    t: (page: string, context: string, fallback?: string) => string;
    currentLanguage: string;
    changeLanguage: (lang: string) => void;
    loading: boolean;
    forceUpdate: number;
    translations: Record<string, unknown>;
    fallbackTranslations: Record<string, unknown>;
    debugTranslations: () => void;
  };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: user } = useUserProfile();

  // Check favorite status using React Query
  const { data: favoriteData } = useQuery({
    queryKey: queryKeys.favorites.byEntity(user?.id ?? 'anonymous', entityType, String(entityId)),
    queryFn: async () => {
      if (!user?.id || !entityId) return null;
      const { data, error } = await supabase
        .from('user_favorites')
        .select('id')
        .eq('user_id', user.id)
        .eq('entity_type', entityType)
        .eq('entity_id', String(entityId))
        .maybeSingle();
      if (error && error.code !== 'PGRST116') {
        return null;
      }
      return data;
    },
    enabled: !!user?.id && !!entityId,
    staleTime: 1000 * 60 * 5,
  });

  const isFavorited = !!favoriteData;

  // Toggle favorite mutation
  const toggleFavoriteMutation = useMutation({
    mutationFn: async (shouldFavorite: boolean) => {
      if (!user?.id || !entityId) throw new Error('User or entity ID missing');

      if (shouldFavorite) {
        const { error } = await supabase.from('user_favorites').insert({
          user_id: user.id,
          entity_type: entityType,
          entity_id: String(entityId),
        });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('entity_type', entityType)
          .eq('entity_id', String(entityId));
        if (error) throw error;
      }
    },
    onSuccess: (_data, shouldFavorite) => {
      // Invalidate favorites queries
      if (user?.id) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.favorites.all(user.id) });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.favorites.byEntity(user.id, entityType, String(entityId)),
        });
      }

      void toast.success(
        shouldFavorite
          ? String(t('favoritebutton', 'added_to_favorites', 'Added to favorites'))
          : String(t('favoritebutton', 'removed_from_favorites', 'Removed from favorites'))
      );
    },
    onError: _error => {
      void toast.error(
        String(t('favoritebutton', 'failed_to_update', 'Failed to update favorites'))
      );
    },
  });

  const handleToggleFavorite = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      void toast.error(
        String(t('favoritebutton', 'sign_in_required', 'Please sign in to save favorites'))
      );
      void navigate('/auth');
      return;
    }

    if (toggleFavoriteMutation.isPending) return;

    toggleFavoriteMutation.mutate(!isFavorited);
  };

  const loading = toggleFavoriteMutation.isPending;

  // Get variant styles based on favorite state
  const getVariantStyles = () => {
    switch (variant) {
      case 'dark':
        return {
          button: '',
          icon: isFavorited ? 'fill-red-500 text-red-500' : COLOR_SCALE['icon-muted'],
        };
      case 'circle':
        return {
          button: `${COLOR_SCALE.surface} text-slate-600 hover:text-slate-900`,
          icon: isFavorited
            ? 'fill-red-500 text-red-500'
            : 'text-slate-600 group-hover:text-slate-900',
        };
      case 'light':
      default:
        return {
          button: isFavorited
            ? `${COLOR_SCALE['red-50-bg']} hover:${COLOR_SCALE['red-100-bg']}`
            : `${COLOR_SCALE.surface} hover:${COLOR_SCALE['surface-muted']}`,
          icon: isFavorited
            ? `fill-red-500 ${COLOR_SCALE['red-500']}`
            : `${COLOR_SCALE['icon-muted']} hover:text-red-500`,
        };
    }
  };

  const variantStyles = getVariantStyles();
  const sizeConfig = getSizeConfig(size);
  const { label } = getAccessibilityLabels(isFavorited, t);

  return (
    <button
      onClick={handleToggleFavorite}
      disabled={loading}
      className={cn(
        'flex items-center justify-center',
        sizeConfig.button,
        RADIUS_SCALE.full,
        INTERACTIVE_STATES.transitions.colors,
        variantStyles.button,
        'focus:outline-none focus:ring-2 focus:ring-white',
        className,
        loading
          ? `${INTERACTIVE_STATES.disabled.opacity} ${INTERACTIVE_STATES.disabled.pointer} ${INTERACTIVE_STATES.disabled.cursor}`
          : 'cursor-pointer'
      )}
      aria-label={label}
      title={label}
    >
      <Heart
        className={`${sizeConfig.icon} transition-all ${INTERACTIVE_STATES.duration.normal} ${variantStyles.icon}`}
      />
    </button>
  );
}
